import { Offer, PopUpProductSequence, PopUpSequenceProduct } from 'common/contracts';
import { EPopupSubType, ESaleDiscountDisplayType } from 'constants/enums';
import Decimal from 'decimal.js';
import { v4 as uuidv4 } from 'uuid';

import {
  DEFAULT_VALUES,
  EPopupTriggersAction
} from '../../PopupsForm.consts';
import { extractPlayerAvailability } from '../../PopupsForm.utils';
import { ProductEntry } from '../usePopupProducts';

interface IPreparedInitialFormValues {
  products: ProductEntry[];
  formValues: any;
}

export const prepareInitialFormValues = (
  result: Offer,
  dup: boolean,
  edit: boolean,
  defaultOfferUiId: string
): IPreparedInitialFormValues => {
  const priceCents = result.productsSequence?.[0]?.priceInUsdCents ?? 0;
  const isDailySubType = result.subType === EPopupSubType.DAILY_BONUS;
  const isStoreRefresh = result.subType === EPopupSubType.STORE_REFRESH;
  const displayRule = result.displayRule;

  const productsSequence = result.productsSequence as PopUpProductSequence[];
  const products = mapSequenceToProductEntries(productsSequence, isDailySubType);

  const formValues = {
    ...DEFAULT_VALUES,
    ...result,
    active: dup || edit ? result.active : true,
    name: dup ? `${result.name}_copy` : result.name,
    displayName: `${dup ? result.displayName ?? result.name : result.displayName ?? result.name}${dup ? '_copy' : ''}`,
    publisherOfferId: dup
      ? `${result.publisherOfferId}_copy`
      : result.publisherOfferId,
    offerUiId:
      result.offerUi?._id || result.offerUi?.offerUiId || defaultOfferUiId,
    price: isDailySubType ? 0 : new Decimal(priceCents).div(100).toNumber(),
    setAsFree: isDailySubType ? false : priceCents === 0,
    products,
    salePercentage: result.productSale?.sale ?? DEFAULT_VALUES.salePercentage,
    salePercentageDisplayType:
      (result.productSale?.type as ESaleDiscountDisplayType) ??
      DEFAULT_VALUES.salePercentageDisplayType,
    discount: result.priceDiscount?.discount ?? DEFAULT_VALUES.discount,
    priceDiscountType:
      result.priceDiscount?.type ?? DEFAULT_VALUES.priceDiscountType,
    subType: result.subType ?? DEFAULT_VALUES.subType,
    triggersAction: isStoreRefresh
      ? EPopupTriggersAction.STORE_REFRESH
      : EPopupTriggersAction.PURCHASE_COMPLETED,
    triggersFrequency: deriveTriggersFrequency(isDailySubType, isStoreRefresh, displayRule, result),
    coolDownInMinutes: deriveCoolDownInMinutes(isStoreRefresh, displayRule, result),
    triggersOfferExternalIds: deriveTriggersOfferExternalIds(isStoreRefresh, result),
    playerAvailability: extractPlayerAvailability(result)
  };

  return { products, formValues };
};

const mapProductToEntry = (product: PopUpSequenceProduct) => ({
  id: uuidv4(),
  productId: product.product?.productId ?? '',
  quantity: product.quantity ?? 0,
  displayName: product.product?.displayName ?? '',
  image: product.product?.images?.[0]?.url ?? '',
  quantityDisplay: product?.quantityDisplay,
  priority: (product as PopUpSequenceProduct & { priority?: string }).priority
});

const mapSequenceToProductEntries = (
  productsSequence: PopUpProductSequence[],
  isDailyBonus: boolean
): ProductEntry[] => {
  if (isDailyBonus) {
    return productsSequence
      .filter((seq) => seq.products.length > 0)
      .map((seq) => mapProductToEntry(seq.products[0] as PopUpSequenceProduct));
  }

  return productsSequence.flatMap((seq) =>
    seq.products.map((product) => mapProductToEntry(product as PopUpSequenceProduct))
  );
};

const deriveTriggersFrequency = (
  isDailySubType: boolean,
  isStoreRefresh: boolean,
  displayRule: Offer['displayRule'],
  result: Offer
): number => {
  if (isDailySubType) return 1;
  if (isStoreRefresh) return displayRule?.frequency ?? 1;
  return result.triggers?.[0]?.every ?? 1;
};

const deriveCoolDownInMinutes = (
  isStoreRefresh: boolean,
  displayRule: Offer['displayRule'],
  result: Offer
): number => {
  if (isStoreRefresh) return displayRule?.cooldownInMinutes ?? 1;
  return (result.coolDownInHours || 0) * 60;
};

const deriveTriggersOfferExternalIds = (
  isStoreRefresh: boolean,
  result: Offer
): string[] => {
  if (isStoreRefresh) return [];
  return result.triggers?.[0]?.rules?.[0]?.value ?? [];
};
